/* Store logo: cached blob URL for the header/snapshot + uploader card for Settings */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Trash2, Store } from "lucide-react";
import { API, useAuth, useStore } from "@/App";
import { Btn, Card } from "@/components/gull/ui";
import { errMsg } from "@/hooks/useLegacy";
import { appConfirm } from "@/components/gull/dialogs";

const cache = {};
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

export const useStoreLogo = () => {
  const { token } = useAuth();
  const { selectedStore } = useStore();
  const id = selectedStore?.id;
  const version = selectedStore?.logo_updated_at || "";
  const [, force] = useState(0);
  useEffect(() => { const fn = () => force((n) => n + 1); listeners.add(fn); return () => listeners.delete(fn); }, []);
  useEffect(() => {
    if (!id || !token || cache[id]?.v === version) return;
    cache[id] = { v: version, url: null };
    axios.get(`${API}/legacy/stores/${id}/logo`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" })
      .then((r) => { cache[id] = { v: version, url: r.status === 200 && r.data?.size ? URL.createObjectURL(r.data) : null }; notify(); })
      .catch(() => { cache[id] = { v: version, url: null }; notify(); });
  }, [id, token, version]);
  return cache[id]?.url || null;
};

export const StoreLogo = ({ className = "h-8" }) => {
  const url = useStoreLogo();
  if (!url) return null;
  return <img data-testid="store-logo" src={url} alt="Store logo" className={`${className} w-auto max-w-[140px] object-contain`} />;
};

export const StoreLogoCard = () => {
  const { token } = useAuth();
  const { selectedStore, selectStore } = useStore();
  const url = useStoreLogo();
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const bump = () => { const s = { ...selectedStore, logo_updated_at: new Date().toISOString() }; selectStore(s); };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await axios.post(`${API}/legacy/stores/${selectedStore.id}/logo`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Logo updated"); bump();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const remove = async () => {
    if (!await appConfirm("Remove the store logo?")) return;
    try { await axios.delete(`${API}/legacy/stores/${selectedStore.id}/logo`, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Logo removed"); bump(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Store Logo" subtitle="Shown in the header, on printed snapshots and in email reports. PNG, JPG or WEBP up to 2 MB." testId="store-logo-card">
      <div className="flex flex-wrap items-center gap-5">
        <div className="w-40 h-20 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 grid place-items-center overflow-hidden">
          {url ? <img data-testid="store-logo-preview" src={url} alt="Store logo" className="max-h-16 max-w-[150px] object-contain" /> : <span className="flex flex-col items-center text-gray-400 text-xs"><Store className="w-6 h-6 mb-1" />No logo yet</span>}
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} data-testid="store-logo-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          <Btn data-testid="store-logo-upload" loading={busy} onClick={() => fileRef.current?.click()}><ImagePlus className="w-4 h-4" />{url ? "Replace logo" : "Upload logo"}</Btn>
          {url && <Btn data-testid="store-logo-remove" color="secondary" onClick={remove}><Trash2 className="w-4 h-4" />Remove</Btn>}
        </div>
      </div>
    </Card>
  );
};
